import React, { useMemo, useState, useEffect } from "react";
import moment from "moment-timezone";
import { Skeleton } from "@/components/ui/skeleton";
import { z } from "zod";
import { Commodities } from "@/lib/api/commodity";
import { SummaryFiltersValidation, useListRiskSummaryFull, useListRiskSummaryFullDetail } from "@/lib/api/risk/summary";
import { get_customer_commodity_top_level, get_commodity_color, get_commodity_border_color, formatPercent, groupable_display_name_to_commodity_list, groupable_display_name_to_drillable_commodity_list, generateDisplayLabelForOtcDetailedType } from "./common";
import { ResponsivePie } from "@nivo/pie";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useQueryParam } from "@/hooks/use-query-param";
import { VisibilityState } from "@tanstack/react-table";
import { formatNumberToDisplay, safeParseDecimal } from "@/lib/api/common";
import { dateSort, DataTable, type DataTableColumn } from "@/components/ui/DataTable/index";
import { ContractTypeOptions } from "@/lib/api/otc";
import { getPath } from "@/lib/prelude";
import { ColumnVisibilityDialog } from "@/components/ui/ColumnVisibilityDialog";
import { Button } from "@/components/ui/button";
import { CommonDestinationTimezone } from "@/lib/utils";
import { PreviewDisplayCard } from "./PreviewDisplayCard";
import { getBrokerageTableColumns } from "./brokerageTableColumns";
import { getGeneralDetailColumns } from "./generalDetailColumns";
import { ContractPreviewDialog } from "./ContractPreviewDialog";
import { getPositionColumns } from "./positionDetailColumns";
import { getRiskColor } from "./colorPalette";

interface CustomerCommodityBreakdownRow {
  customer_id: string;
  commodity_type: string;
  commodity_display_name: string;

  acreage: number;
  projected_production: number;

  percent_hedged: number;
  hedged_without_beacon_quantity: number;
  hedged_without_beacon_percent: number;

  open_brokerage_quantity: number;
  open_brokerage_percent: number;

  closed_brokerage_quantity: number;
  closed_brokerage_percent: number;

  cash_quantity: number;
  cash_percent: number;

  hta_quantity: number;
  hta_percent: number;

  basis_quantity: number;
  basis_percent: number;

  beacon_quantity: number;
  beacon_percent: number;

  unsold_quantity: number;
  unsold_percent: number;

  offer_up_quantity: number;
  offer_up_percent: number;
}

export function CustomerFullDetailView({ baseTopLevelResult, customerId, commodityDisplayName, top_level_filters }: { baseTopLevelResult: ReturnType<typeof useListRiskSummaryFull>, customerId: string, commodityDisplayName: string, top_level_filters: z.infer<typeof SummaryFiltersValidation> }) {
  const summaryData = baseTopLevelResult?.data?.data?.summary || {};
  const [_commodityDisplayName, setSelectedCommodityDisplayName] = useQueryParam("commodity", "");

  const { isReady, customerName, commodity_top_level_items } = useMemo(() => {
    if (!summaryData?.commodity_list) return { isReady: false, customerName: "Loading", commodity_top_level_items: [] };
    if (!summaryData?.customer_name_map) return { isReady: false, customerName: "Loading", commodity_top_level_items: [] };

    const customerName = customerId ? summaryData.customer_name_map[customerId] || "Unknown Customer" : "";

    const commodity_top_level_items = summaryData.commodity_list.map((commodity_type: Commodities) => get_customer_commodity_top_level({ commodity_type, customer_id: customerId, summaryData })).filter(Boolean).sort((a: any, b: any) => a.commodity_display_name.localeCompare(b.commodity_display_name));

    return { isReady: true, customerName, commodity_top_level_items };
  }, [summaryData]);

  return (
    <div className="flex flex-col gap-1">
      <h2 className="text-xl font-bold">{isReady ? null : (
        <Skeleton className="h-4 w-[80px]" />
      )}</h2>
      {!isReady && (
        <div className="flex flex-col space-y-2">
          <Skeleton className="h-4 w-[250px]" />
          <Skeleton className="h-4 w-[200px]" />
        </div>
      )}
      {isReady && (
        <div className="">
          <Tabs value={commodityDisplayName} onValueChange={(tab) => setSelectedCommodityDisplayName(tab)} >
            <div className="flex flex-row justify-between mb-4 max-w-full">
              <TabsList>
                <TabsTrigger value="Corn">Corn</TabsTrigger>
                <TabsTrigger value="Soybeans">Soybeans</TabsTrigger>
                <TabsTrigger value="Wheat">Wheat</TabsTrigger>
                <TabsTrigger value="Canola">Canola</TabsTrigger>
                <TabsTrigger value="Barley">Barley</TabsTrigger>
                <TabsTrigger value="Durum">Durum</TabsTrigger>
                <TabsTrigger value="Sunflowers">Sunflowers</TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="Corn" className="max-w-full">
              <CustomerCommodityViewCards
                customerId={customerId}
                commodityDisplayName={commodityDisplayName}
                baseTopLevelResult={baseTopLevelResult}
                top_level_filters={top_level_filters}
              />
            </TabsContent>
            <TabsContent value="Soybeans">
              <CustomerCommodityViewCards
                customerId={customerId}
                commodityDisplayName={commodityDisplayName}
                baseTopLevelResult={baseTopLevelResult}
                top_level_filters={top_level_filters}
              />
            </TabsContent>
            <TabsContent value="Wheat">
              <CustomerCommodityViewCards
                customerId={customerId}
                commodityDisplayName={commodityDisplayName}
                baseTopLevelResult={baseTopLevelResult}
                top_level_filters={top_level_filters}
              />
            </TabsContent>
            <TabsContent value="Canola">
              <CustomerCommodityViewCards
                customerId={customerId}
                commodityDisplayName={commodityDisplayName}
                baseTopLevelResult={baseTopLevelResult}
                top_level_filters={top_level_filters}
              />
            </TabsContent>
            <TabsContent value="Barley">
              <CustomerCommodityViewCards
                customerId={customerId}
                commodityDisplayName={commodityDisplayName}
                baseTopLevelResult={baseTopLevelResult}
                top_level_filters={top_level_filters}
              />
            </TabsContent>
            <TabsContent value="Sunflowers">
              <CustomerCommodityViewCards
                customerId={customerId}
                commodityDisplayName={commodityDisplayName}
                baseTopLevelResult={baseTopLevelResult}
                top_level_filters={top_level_filters}
              />
            </TabsContent>
            <TabsContent value="Durum">
              <CustomerCommodityViewCards
                customerId={customerId}
                commodityDisplayName={commodityDisplayName}
                baseTopLevelResult={baseTopLevelResult}
                top_level_filters={top_level_filters}
              />
            </TabsContent>
          </Tabs>
        </div>
      )}
    </div>
  );
}

export function CustomerCommodityViewCards({ top_level_filters, baseTopLevelResult, customerId, commodityDisplayName }: { top_level_filters: z.infer<typeof SummaryFiltersValidation>, baseTopLevelResult: ReturnType<typeof useListRiskSummaryFull>, customerId: string, commodityDisplayName: string }) {
  const summaryData = baseTopLevelResult?.data?.data?.summary || {};
  const [detailView, setDetailView] = useQueryParam("detail_view", "");
  // Map reown_<contract_type> to just "reown" for the API call
  const apiDetailView = detailView.startsWith("reown") ? "reown" : detailView;
  const detailedSummaryResult = useListRiskSummaryFullDetail({
    filters: { ...top_level_filters, detail_view: apiDetailView }
  });
  const detailedData = detailedSummaryResult?.data?.data?.full_detail || {};

  const { isReady } = useMemo(() => {
    if (!summaryData?.commodity_list) return { isReady: false };
    if (!summaryData?.customer_name_map) return { isReady: false };
    if (!detailedData.detail_view) return { isReady: false };

    return { isReady: true };
  }, [summaryData, customerId, commodityDisplayName, detailedData]);


  if (!isReady) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => (
          <Skeleton key={i} className="h-32 w-full" />
        ))}
      </div>
    );
  }

  if (detailView.startsWith("otc_") || detailView === "otc") {
    return (<OtcDetailView otcType={detailView.split("otc_")[1]} detailedSummaryResult={detailedSummaryResult} baseTopLevelResult={baseTopLevelResult} detailView={detailView} />)
  } else if (detailView.startsWith("otc-")) {
    return (<OtcDetailView otcType={detailView} detailedSummaryResult={detailedSummaryResult} baseTopLevelResult={baseTopLevelResult} detailView={detailView} />)
  }

  if (detailView === "cash") {
    return (<CashDetailView detailedSummaryResult={detailedSummaryResult} baseTopLevelResult={baseTopLevelResult} detailView={detailView} />)
  }

  if (/open_brokerage|closed_brokerage/.test(detailView)) {
    return (<BrokerageDetailView detailedSummaryResult={detailedSummaryResult} baseTopLevelResult={baseTopLevelResult} detailView={detailView} />)
  }

  if (/basis|hta|beacon|dp|direct/.test(detailView)) {
    return (<GeneralDetailView detailedSummaryResult={detailedSummaryResult} baseTopLevelResult={baseTopLevelResult} detailView={detailView} />)
  }

  if (/manual_otc_offer_ups|manual_open_offers|base_open_offers|open_offers/img.test(detailView)) {
    return (<PositionsView detailedSummaryResult={detailedSummaryResult} baseTopLevelResult={baseTopLevelResult} detailView={detailView} />)
  }

  if (detailView.startsWith("reown")) {
    const reownContractTypeFilter = detailView.startsWith("reown_") ? detailView.slice("reown_".length) : undefined;
    return (<ReownDetailView detailedSummaryResult={detailedSummaryResult} baseTopLevelResult={baseTopLevelResult} detailView={detailView} contractTypeFilter={reownContractTypeFilter} />)
  }

  return (
    <div className="space-y-6">
      Unknown Detail View: {detailView}
    </div >
  );
}

function BackButton() {
  const [_detailView, setDetailView] = useQueryParam("detail_view", "");

  return (
    <Button variant="outline" onClick={() => setDetailView("")}>
      Back
    </Button>
  );
}

function PreviewDisplayCardWrapper({ detailView, baseTopLevelResult, customerId, commodityDisplayName, nameMetaAppendix }: { detailView: string; baseTopLevelResult: ReturnType<typeof useListRiskSummaryFull>, customerId: string, commodityDisplayName: string; nameMetaAppendix?: string }) {
  const summaryData = baseTopLevelResult?.data?.data?.summary || {};
  const [_detailView, setDetailView] = useQueryParam("detail_view", "");

  const { isReady, commodityData, otcInfo } = useMemo(() => {
    if (!summaryData?.commodity_list) return { isReady: false, commodityData: null };
    if (!summaryData?.customer_name_map) return { isReady: false, commodityData: null };

    const groupable_commodity_type = groupable_display_name_to_drillable_commodity_list(commodityDisplayName);

    const item = get_customer_commodity_top_level({
      commodity_type: groupable_commodity_type[0],
      customer_id: customerId,
      summaryData
    });


    const otc_type = detailView.startsWith("otc_") ? detailView.split("otc_")[1] : null;
    const otcInfo: any = detailView.startsWith("otc_") ? Object.entries(item?.otc_full_map_stats || {}).find(([otcType, stats]: [string, any]) => {
      return otcType === otc_type;
    }) : null;

    return { isReady: true, commodityData: item, otcInfo };
  }, [summaryData, customerId, commodityDisplayName]);

  if (!isReady || !commodityData) {
    return null;
  }

  return (
    <PreviewDisplayCard detailViewToRender={detailView} commodityData={commodityData} otcInfo={otcInfo} nameMetaAppendix={nameMetaAppendix} />
  )

}

function OtcDetailView({ otcType, detailedSummaryResult, baseTopLevelResult, detailView }: { otcType: string, detailedSummaryResult: ReturnType<typeof useListRiskSummaryFullDetail>, baseTopLevelResult: ReturnType<typeof useListRiskSummaryFull>, detailView: string }) {
  const [commodityDisplayName] = useQueryParam("commodity", "");
  const [customerId] = useQueryParam("customer_ids", "");
  const [selectedContract, setSelectedContract] = useState<any>(null);
  const [isContractDialogOpen, setIsContractDialogOpen] = useState(false);

  const rows = detailedSummaryResult?.data?.data?.full_detail?.detailed_cash_contracts || [];

  const { columns, knownOtcRowsMap } = useMemo(() => {
    const knownOtcRowsMap = new Map<string, any>((detailedSummaryResult?.data?.data?.full_detail?.detailed_otc_contracts || []).map((row: any) => [row.contract_no, row]));

    const columns: DataTableColumn<any>[] = [
      commodityDisplayName === 'Wheat' ? {
        id: "commodity",
        accessorKey: 'commodity',
        header: "Commodity",
      } : null!,
      {
        id: "contract_type",
        accessorKey: 'contract_type',
        header: "Contract Type",
        enableColumnFilter: false,
        cell: (info) => {
          return (
            <span className="text-gray-600 text-sm">
              {generateDisplayLabelForOtcDetailedType(info.row.original.detailed_contract_type || info.row.original.contract_type)}
            </span>
          )
        },
      } as DataTableColumn<any>,
      {
        id: "contract_number",
        header: "Contract #",
        accessorFn: (row: any) => row.contract_no || row.contract_number,
      },
      {
        id: "contract_release",
        accessorKey: "contract_release",
        header: "Rel",
      },
      {
        id: "is_open",
        header: "Open",
        accessorFn: (row: any) => row.is_open ? "Open" : "Closed",
        cell: (info) => {
          return (
            <span className="text-gray-600 text-sm">
              {info.getValue() as string}
            </span>
          )
        },
      } as DataTableColumn<any>,
      {
        id: "otc_step_one",
        header: "Leg 1",
        accessorFn: (row: any) => knownOtcRowsMap.has(row.contract_number) ? knownOtcRowsMap.get(row.contract_number).step_one || "" : "",
        cell: (info) => {
          return (
            <span className="text-gray-600 text-sm">
              {info.getValue() ? formatNumberToDisplay(info.getValue() as string, { passthroughEmpty: true }) : ""}
            </span>
          )
        },
      } as DataTableColumn<any>,
      {
        id: "otc_step_two",
        header: "Leg 2",
        accessorFn: (row: any) => knownOtcRowsMap.has(row.contract_number) ? knownOtcRowsMap.get(row.contract_number).step_two || "" : "",
        cell: (info) => {
          return (
            <span className="text-gray-600 text-sm">
              {info.getValue() ? formatNumberToDisplay(info.getValue() as string, { passthroughEmpty: true }) : ""}
            </span>
          )
        },
      } as DataTableColumn<any>,
      {
        id: "otc_step_three",
        header: "Leg 3",
        accessorFn: (row: any) => knownOtcRowsMap.has(row.contract_number) ? knownOtcRowsMap.get(row.contract_number).step_three || "" : "",
        cell: (info) => {
          return (
            <span className="text-gray-600 text-sm">
              {info.getValue() ? formatNumberToDisplay(info.getValue() as string, { passthroughEmpty: true }) : ""}
            </span>
          )
        },
      } as DataTableColumn<any>,
      {
        id: "otc_double_up",
        header: "Double Up",
        accessorFn: (row: any) => knownOtcRowsMap.has(row.contract_number) ? knownOtcRowsMap.get(row.contract_number).double_up || "" : "",
        cell: (info) => {
          return (
            <span className="text-gray-600 text-sm">
              {info.getValue() ? formatNumberToDisplay(info.getValue() as string, { passthroughEmpty: true }) : ""}
            </span>
          )
        },
      } as DataTableColumn<any>,
      {
        id: "option_month",
        accessorKey: 'option_month',
        header: "Option Month",
      },
      {
        id: "contract_date",
        accessorKey: 'contract_date',
        header: "Contract Date",
        cell: (info) => {
          return (
            <span className="text-gray-600 text-sm">
              {info.getValue() ? moment.tz(info.getValue(), CommonDestinationTimezone).format("MM/DD/YYYY") : ''}
            </span>
          )
        },
      } as DataTableColumn<any>,
      {
        id: "quantity",
        accessorKey: 'quantity',
        header: "Quantity",
        cell: (info) => {
          return (
            <span className="text-gray-600 text-sm">
              {formatNumberToDisplay(info.getValue() as string)}
            </span>
          )
        },
      } as DataTableColumn<any>,
      {
        id: "balance",
        accessorKey: 'balance',
        header: "Balance",
        cell: (info) => {
          return (
            <span className="text-gray-600 text-sm">
              {formatNumberToDisplay(info.getValue() as string)}
            </span>
          )
        },
      } as DataTableColumn<any>,
      {
        id: "futures",
        accessorKey: 'futures',
        header: "Futures",
        cell: (info) => {
          const amount = /cnl|canola/im.test(info.row.original.commodity || "") ?
                         formatNumberToDisplay(info.getValue() as string, { minimumFractionDigits: 4, maximumFractionDigits: 4 }) :
                         formatNumberToDisplay(info.getValue() as string);

          return (
            <span className="text-gray-600 text-sm">
              {amount}
            </span>
          )
        },
      } as DataTableColumn<any>,
      {
        id: "basis",
        accessorKey: 'basis',
        header: "Basis",
        cell: (info) => {
          const amount = /cnl|canola/im.test(info.row.original.commodity || "") ?
                         formatNumberToDisplay(info.getValue() as string, { minimumFractionDigits: 4, maximumFractionDigits: 4 }) :
                         formatNumberToDisplay(info.getValue() as string);

          return (
            <span className="text-gray-600 text-sm">
              {amount}
            </span>
          )
        },
      } as DataTableColumn<any>,
      {
        id: "implied_cost",
        accessorKey: "implied_cost",
        header: "Revenue",
        cell: (info) => {
          return (
            <span className="text-gray-600 text-sm">
              {formatNumberToDisplay(info.getValue() as string || "0")}
            </span>
          )
        },
      } as DataTableColumn<any>,
      {
        id: "implied_futures",
        accessorKey: "implied_futures",
        header: "Implied Futures",
        cell: (info) => {
          const amount = /cnl|canola/im.test(info.row.original.commodity || "") ?
                         formatNumberToDisplay(info.getValue() as string, { minimumFractionDigits: 4, maximumFractionDigits: 4 }) :
                         formatNumberToDisplay(info.getValue() as string);


          return (
            <span className="text-gray-600 text-sm">
              {amount}
            </span>
          )
        },
      } as DataTableColumn<any>,
      {
        id: "implied_basis",
        accessorKey: "implied_basis",
        header: "Implied Basis",
        cell: (info) => {
          return (
            <span className="text-gray-600 text-sm">
              {formatNumberToDisplay(info.getValue() as string || "0", { maximumFractionDigits: 4 })}
            </span>
          )
        },
      } as DataTableColumn<any>,
      {
        id: "implied_fx_futures",
        accessorKey: "implied_fx_futures",
        header: "Implied FX Futures",
        cell: (info) => {
          return (
            <span className="text-gray-600 text-sm">
              {formatNumberToDisplay(info.getValue() as string || "0", { maximumFractionDigits: 4 })}
            </span>
          )
        },
      } as DataTableColumn<any>,
      {
        id: "implied_fx_futures_contract",
        accessorKey: "implied_fx_futures_contract",
        header: "Implied FX Futures Contract",
        cell: (info) => {
          return (
            <span className="text-gray-600 text-sm">
                {info.getValue() as string || "--"}
            </span>
          )
        },
      } as DataTableColumn<any>,
      {
        id: "start_date",
        accessorKey: 'start_date',
        header: "Start Date",
        cell: (info) => {
          return (
            <span className="text-gray-600 text-sm">
              {info.getValue() ? moment.tz(info.getValue() as string, CommonDestinationTimezone).format("MM/DD/YYYY") : ''}
            </span>
          )
        },
      } as DataTableColumn<any>,
      {
        id: "end_date",
        accessorKey: 'end_date',
        header: "End Date",
        cell: (info) => {
          return (
            <span className="text-gray-600 text-sm">
              {info.getValue() ? moment.tz(info.getValue() as string, CommonDestinationTimezone).format("MM/DD/YYYY") : ''}
            </span>
          )
        },
      } as DataTableColumn<any>,
    ].filter(Boolean);
    return { columns, knownOtcRowsMap };
  }, [detailedSummaryResult, commodityDisplayName]);

  const handleCellClick = (row: any) => {
    const contractNumber = row.contract_number;
    if (contractNumber && knownOtcRowsMap.has(contractNumber)) {
      const otcContract = knownOtcRowsMap.get(contractNumber);
      setSelectedContract(otcContract);
      setIsContractDialogOpen(true);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="max-w-[400px]">
        <PreviewDisplayCardWrapper detailView={detailView} baseTopLevelResult={baseTopLevelResult} customerId={customerId} commodityDisplayName={commodityDisplayName} />
      </div>
      <div className="max-w-full flex flex-col justify-between">
        <DataTable
          defaultColumnVisibility={generateDefaultDetailColumnVisiblity(detailView, commodityDisplayName) as any}
          columns={columns as any}
          data={rows!}
          stickyHeaders
          enableColumnVisibility
          enableSorting
          enableMultiSort
          enableFiltering
          enableGrouping
          virtualizationRowHeight={49}
          virtualizationOverscan={2}
          enableVirtualization
          enableColumnResizing
          enableExport
          filterStyle='select'
          filterMode='internal'
          className=""
          tableClassName={"max-h-[800px] overflow-y-auto"}
          rowClickable
          onRowClick={handleCellClick}
        />
      </div>

      <ContractPreviewDialog
        isOpen={isContractDialogOpen}
        onClose={() => {
          setIsContractDialogOpen(false);
          setSelectedContract(null);
        }}
        contract={selectedContract}
      />
    </div>
  );
}

function CashDetailView({ detailView, detailedSummaryResult, baseTopLevelResult }: { detailView: string; detailedSummaryResult: ReturnType<typeof useListRiskSummaryFullDetail>, baseTopLevelResult: ReturnType<typeof useListRiskSummaryFull> }) {
  const [commodityDisplayName] = useQueryParam("commodity", "");
  const [customerId] = useQueryParam("customer_ids", "");

  const rows = detailedSummaryResult?.data?.data?.full_detail?.detailed_cash_contracts || [];
  const otc_contracts = detailedSummaryResult?.data?.data?.full_detail?.detailed_otc_contracts || [];

  const { columns } = useMemo(() => {
    const knownOtcRowsMap = new Map<string, any>((otc_contracts).map((row: any) => [row.contract_no, row]));
    const columns: DataTableColumn<any>[] = [
      commodityDisplayName === 'Wheat' ? {
        id: "commodity",
        accessorKey: "commodity",
        header: "Commodity",
        footer: (props: any) => props.column.id,
      } : null!,
      {
        id: "contract_type",
        accessorKey: "contract_type",
        header: "Contract Type",
        footer: (props: any) => props.column.id,
        cell: (info) => {
          return (
            <span className="text-gray-600 text-sm">
              {generateDisplayLabelForOtcDetailedType(info.row.original.detailed_contract_type || info.row.original.contract_type)}
            </span>
          )
        },
      } as DataTableColumn<any>,
      {
        id: "contract_number",
        accessorKey: "contract_number",
        header: "Contract Number",
      },
      {
        id: "is_open",
        header: "Open",
        accessorFn: (row: any) => row.is_open ? "Open" : "Closed",
        cell: (info) => {
          return (
            <span className="text-gray-600 text-sm">
              {info.getValue() as string}
            </span>
          )
        },
      } as DataTableColumn<any>,
      {
        id: "option_month",
        accessorKey: "option_month",
        header: "Option Month",
      },
      {
        id: "contract_date",
        accessorKey: "contract_date",
        header: "Contract Date",
        cell: (info) => {
          return (
            <span className="text-gray-600 text-sm">
              {info.getValue() ? moment.tz(info.getValue(), CommonDestinationTimezone).format("MM/DD/YYYY") : ''}
            </span>
          )
        },
      } as DataTableColumn<any>,
      {
        id: "quantity",
        accessorKey: "quantity",
        header: "Quantity",
        cell: (info) => {
          return (
            <span className="text-gray-600 text-sm">
              {formatNumberToDisplay(info.getValue() as string)}
            </span>
          )
        },
      } as DataTableColumn<any>,
      {
        id: "balance",
        accessorKey: "balance",
        header: "Balance",
        cell: (info) => {
          return (
            <span className="text-gray-600 text-sm">
              {formatNumberToDisplay(info.getValue() as string)}
            </span>
          )
        },
      } as DataTableColumn<any>,
      {
        id: "futures",
        accessorKey: 'futures',
        header: "Futures",
        cell: (info) => {
          const amount = /cnl|canola/im.test(info.row.original.commodity || "") ?
                         formatNumberToDisplay(info.getValue() as string, { minimumFractionDigits: 4, maximumFractionDigits: 4 }) :
                         formatNumberToDisplay(info.getValue() as string);

          return (
            <span className="text-gray-600 text-sm">
              {amount}
            </span>
          )
        },
      } as DataTableColumn<any>,
      {
        id: "basis",
        accessorKey: "basis",
        header: "Basis",
      },
      {
        id: "price",
        accessorKey: "price",
        header: "Price",
      },
      {
        id: "implied_cost",
        accessorKey: "implied_cost",
        header: "Revenue",
        cell: (info) => {
          return (
            <span className="text-gray-600 text-sm">
              {formatNumberToDisplay(info.getValue() as string || "0")}
            </span>
          )
        },
      } as DataTableColumn<any>,
      {
        id: "start_date",
        accessorKey: "start_date",
        header: "Start Date",
        cell: (info) => {
          return (
            <span className="text-gray-600 text-sm">
              {info.getValue() ? moment.tz(info.getValue() as string, CommonDestinationTimezone).format("MM/DD/YYYY") : ''}
            </span>
          )
        },
      } as DataTableColumn<any>,
      {
        id: "end_date",
        accessorKey: "end_date",
        header: "End Date",
        cell: (info) => {
          return (
            <span className="text-gray-600 text-sm">
              {info.getValue() ? moment.tz(info.getValue() as string, CommonDestinationTimezone).format("MM/DD/YYYY") : ''}
            </span>
          )
        },
      } as DataTableColumn<any>,
    ].filter(Boolean);
    return { columns };
  }, [otc_contracts]);


  return (
    <div className="flex flex-col gap-4">
      <div className="max-w-[400px]">
        <PreviewDisplayCardWrapper detailView={detailView} baseTopLevelResult={baseTopLevelResult} customerId={customerId} commodityDisplayName={commodityDisplayName} />
      </div>
      <div className="flex flex-col justify-between">
        <DataTable
          columns={columns as any}
          data={rows!}
          stickyHeaders
          enableColumnVisibility
          enableSorting
          enableMultiSort
          enableFiltering
          enableGrouping
          virtualizationRowHeight={49}
          virtualizationOverscan={2}
          enableExport
          filterStyle='select'
          filterMode='internal'
          className=""
          tableClassName={"max-h-[800px] overflow-y-auto"}
        />
      </div>
    </div>
  );
}


function PositionsView({ detailView, detailedSummaryResult, baseTopLevelResult }: { detailView: string; detailedSummaryResult: ReturnType<typeof useListRiskSummaryFullDetail>, baseTopLevelResult: ReturnType<typeof useListRiskSummaryFull> }) {
  const [commodityDisplayName] = useQueryParam("commodity", "");
  const [customerId] = useQueryParam("customer_ids", "");

  const manual_rows = detailedSummaryResult?.data?.data?.full_detail?.detailed_manual_positions || [];
  const client_rows = detailedSummaryResult?.data?.data?.full_detail?.detailed_client_positions || [];
  const inferred_rows = detailedSummaryResult?.data?.data?.full_detail?.detailed_inferred_offer_ups || [];
  const { rows } = useMemo(() => {
    return {
      rows: [
        ...manual_rows,
        ...client_rows,
        ...inferred_rows,
      ]
    }
  }, [
    manual_rows,
    client_rows,
    inferred_rows,
  ]);


  const columns = getPositionColumns({ disableActions: true, handleDelete: undefined });

  return (
    <div className="flex flex-col gap-4">

      <div className="max-w-[400px]">
        <PreviewDisplayCardWrapper detailView={detailView} baseTopLevelResult={baseTopLevelResult} customerId={customerId} commodityDisplayName={commodityDisplayName} />
      </div>

      <div className="flex flex-col justify-between">
        <DataTable
          defaultColumnVisibility={{ actions: false }}
          defaultSorting={[
            { id: "customer_id", desc: false }, { id: "commodity", desc: false },
            { id: "contract_number", desc: true }, { id: "contract_release", desc: false },
            { id: "created_at", desc: false }
          ]}
          columns={columns as any}
          data={rows!}
          stickyHeaders
          enableColumnVisibility
          enableSorting
          enableMultiSort
          enableFiltering
          enableGrouping
          virtualizationRowHeight={49}
          virtualizationOverscan={2}
          enableExport
          filterStyle='select'
          filterMode='internal'
          className=""
          tableClassName={"max-h-[800px] overflow-y-auto"}
          emptyMessage={"No positions found"}
        />
      </div>
    </div>
  );
}


function GeneralDetailView({ detailView, detailedSummaryResult, baseTopLevelResult }: { detailView: string; detailedSummaryResult: ReturnType<typeof useListRiskSummaryFullDetail>, baseTopLevelResult: ReturnType<typeof useListRiskSummaryFull> }) {
  const [commodityDisplayName] = useQueryParam("commodity", "");
  const [customerId] = useQueryParam("customer_ids", "");
  const [selectedContract, setSelectedContract] = useState<any>(null);
  const [isContractDialogOpen, setIsContractDialogOpen] = useState(false);
  const rows = detailedSummaryResult?.data?.data?.full_detail?.detailed_cash_contracts || [];

  const { knownOtcRowsMap } = useMemo(() => {
    const knownOtcRowsMap = new Map<string, any>((detailedSummaryResult?.data?.data?.full_detail?.detailed_otc_contracts || []).map((row: any) => [row.contract_no, row]));
    return { knownOtcRowsMap };
  }, [detailedSummaryResult, commodityDisplayName]);

  const lookupContractType = (contractNumber?: string) => {
    if (!contractNumber) return "";
  };

  const columns = getGeneralDetailColumns({
    commodityDisplayName,
    contractTypeLookupByNumber: lookupContractType,
    meta: { isCellClick: true },
  });

  const handleCellClick = (row: any) => {
    const contractNumber = row.contract_number;
    if (contractNumber && knownOtcRowsMap.has(contractNumber)) {
      const otcContract = knownOtcRowsMap.get(contractNumber);
      setSelectedContract(otcContract);
      setIsContractDialogOpen(true);
    }
  };

  return (
    <div className="flex flex-col gap-4">

      <div className="max-w-[400px]">
        <PreviewDisplayCardWrapper detailView={detailView} baseTopLevelResult={baseTopLevelResult} customerId={customerId} commodityDisplayName={commodityDisplayName} />
      </div>

      <div className="flex flex-col justify-between">
        <DataTable
          defaultColumnVisibility={generateDefaultDetailColumnVisiblity(detailView, commodityDisplayName) as any}
          columns={columns as any}
          data={rows!}
          stickyHeaders
          enableColumnVisibility
          enableSorting
          enableMultiSort
          enableFiltering
          enableGrouping
          virtualizationRowHeight={49}
          virtualizationOverscan={2}
          enableExport
          filterStyle='select'
          filterMode='internal'
          className=""
          tableClassName={"max-h-[800px] overflow-y-auto"}
          rowClickable
          onRowClick={handleCellClick}
        />
      </div>

      <ContractPreviewDialog
        isOpen={isContractDialogOpen}
        onClose={() => {
          setIsContractDialogOpen(false);
          setSelectedContract(null);
        }}
        contract={selectedContract}
      />
    </div>
  );
}

function BrokerageDetailView({ detailView, detailedSummaryResult, baseTopLevelResult }: { detailView: string; detailedSummaryResult: ReturnType<typeof useListRiskSummaryFullDetail>, baseTopLevelResult: ReturnType<typeof useListRiskSummaryFull> }) {
  const [commodityDisplayName] = useQueryParam("commodity", "");
  const [customerId] = useQueryParam("customer_ids", "");
  const rows = detailedSummaryResult?.data?.data?.full_detail?.detailed_client_positions || [];
  const detailedCustomer = baseTopLevelResult?.data?.data?.summary?.detailed_customer_info || {};
  const nameMetaAppendix: string | undefined = detailedCustomer?.active_account_codes?.[0] ?? "";

  const columns = getBrokerageTableColumns(commodityDisplayName);
  return (
    <div className="flex flex-col gap-4">
      <div className="max-w-[400px]">
        <PreviewDisplayCardWrapper detailView={detailView} baseTopLevelResult={baseTopLevelResult} customerId={customerId} commodityDisplayName={commodityDisplayName} nameMetaAppendix={nameMetaAppendix ? ` | RJO #: ${nameMetaAppendix}` : ""} />
      </div>
      <div className="flex flex-col justify-between">
        <DataTable
          defaultColumnVisibility={{ actions: false }}
          columns={columns as any}
          data={rows!}
          stickyHeaders
          enableColumnVisibility
          enableSorting
          enableMultiSort
          enableFiltering
          enableGrouping
          virtualizationRowHeight={49}
          virtualizationOverscan={2}
          enableExport
          filterStyle='select'
          filterMode='internal'
          className=""
          tableClassName={"max-h-[800px] overflow-y-auto"}
        />
      </div>
    </div>
  );
}

function ReownDetailView({ detailedSummaryResult, baseTopLevelResult, detailView, contractTypeFilter }: { detailedSummaryResult: ReturnType<typeof useListRiskSummaryFullDetail>, baseTopLevelResult: ReturnType<typeof useListRiskSummaryFull>, detailView: string, contractTypeFilter?: string }) {
  const [commodityDisplayName] = useQueryParam("commodity", "");
  const [customerId] = useQueryParam("customer_ids", "");

  const allReownContracts: any[] = detailedSummaryResult?.data?.data?.full_detail?.detailed_reown_contracts || [];

  const rows = contractTypeFilter
    ? allReownContracts.filter((row: any) => row.contract_type === contractTypeFilter)
    : allReownContracts;

  const columns: DataTableColumn<any>[] = [
    {
      id: "crop_year",
      accessorKey: "crop_year",
      header: "Crop Year",
    },
    {
      id: "contract_no",
      accessorKey: "contract_no",
      header: "Contract #",
    },
    {
      id: "quantity",
      accessorKey: "quantity",
      header: "Quantity",
      cell: (info) => (
        <span className="text-gray-600 text-sm">
          {formatNumberToDisplay(info.getValue() as string)}
        </span>
      ),
    } as DataTableColumn<any>,
    {
      id: "futures",
      accessorKey: "futures",
      header: "Futures",
      cell: (info) => (
        <span className="text-gray-600 text-sm">
          {formatNumberToDisplay(info.getValue() as string, { passthroughEmpty: true })}
        </span>
      ),
    } as DataTableColumn<any>,
    {
      id: "basis",
      accessorKey: "basis",
      header: "Basis",
      cell: (info) => (
        <span className="text-gray-600 text-sm">
          {formatNumberToDisplay(info.getValue() as string, { passthroughEmpty: true })}
        </span>
      ),
    } as DataTableColumn<any>,
    {
      id: "step_one",
      accessorKey: "step_one",
      header: "Leg 1",
      cell: (info) => (
        <span className="text-gray-600 text-sm">
          {info.getValue() ? formatNumberToDisplay(info.getValue() as string, { passthroughEmpty: true }) : ""}
        </span>
      ),
    } as DataTableColumn<any>,
    {
      id: "step_two",
      accessorKey: "step_two",
      header: "Leg 2",
      cell: (info) => (
        <span className="text-gray-600 text-sm">
          {info.getValue() ? formatNumberToDisplay(info.getValue() as string, { passthroughEmpty: true }) : ""}
        </span>
      ),
    } as DataTableColumn<any>,
    {
      id: "step_three",
      accessorKey: "step_three",
      header: "Leg 3",
      cell: (info) => (
        <span className="text-gray-600 text-sm">
          {info.getValue() ? formatNumberToDisplay(info.getValue() as string, { passthroughEmpty: true }) : ""}
        </span>
      ),
    } as DataTableColumn<any>,
    {
      id: "double_up",
      accessorKey: "double_up",
      header: "Double Up",
      cell: (info) => (
        <span className="text-gray-600 text-sm">
          {info.getValue() ? formatNumberToDisplay(info.getValue() as string, { passthroughEmpty: true }) : ""}
        </span>
      ),
    } as DataTableColumn<any>,
    {
      id: "created_at",
      accessorKey: "created_at",
      header: "Created",
      sortingFn: "datetime",
      cell: (info) => (
        <span className="text-gray-600 text-sm">
          {info.getValue() ? moment.tz(info.getValue() as string, CommonDestinationTimezone).format("MM/DD/YYYY") : ""}
        </span>
      ),
    } as DataTableColumn<any>,
  ].filter(Boolean);

  const summaryData = baseTopLevelResult?.data?.data?.summary || {};
  const reownPreviewData = (() => {
    if (!summaryData?.commodity_list || !summaryData?.customer_name_map) return null;
    const groupable_commodity_type = groupable_display_name_to_drillable_commodity_list(commodityDisplayName);
    const item = get_customer_commodity_top_level({
      commodity_type: groupable_commodity_type[0],
      customer_id: customerId,
      summaryData
    });
    if (!item || !contractTypeFilter) return null;
    const stats = item.reown_stats[contractTypeFilter];
    if (!stats) return null;
    return {
      label: generateDisplayLabelForOtcDetailedType(contractTypeFilter) || contractTypeFilter,
      quantity: safeParseDecimal({})(stats.quantity || 0).toNumber(),
    };
  })();

  const reownColors = getRiskColor('reown');

  return (
    <div className="flex flex-col gap-4">
      {reownPreviewData && (
        <div className="max-w-[400px]">
          <div className={`${reownColors.background} border ${reownColors.border} rounded-lg p-4`}>
            <div className={`${reownColors.text} font-medium text-sm mb-1`}>{reownPreviewData.label}</div>
            <div className={`${reownColors.textBold} font-bold text-xl`}>{formatNumberToDisplay(reownPreviewData.quantity, { emptyFormat: '--', zeroFormat: '--' })}</div>
          </div>
        </div>
      )}
      <div className="max-w-full flex flex-col justify-between">
        <DataTable
          columns={columns as any}
          data={rows}
          stickyHeaders
          enableColumnVisibility
          enableSorting
          enableMultiSort
          enableFiltering
          enableGrouping
          virtualizationRowHeight={49}
          virtualizationOverscan={2}
          enableVirtualization
          enableColumnResizing
          enableExport
          filterStyle='select'
          filterMode='internal'
          className=""
          tableClassName={"max-h-[800px] overflow-y-auto"}
          emptyMessage={"No reown contracts found"}
        />
      </div>
    </div>
  );
}

function generateDefaultDetailColumnVisiblity(detailView: string, commodity: string) {
  const commodity_visibility_fields = /canola/i.test(commodity || "") ? ({}) : ({ implied_fx_futures: false, implied_fx_futures_contract: false });

  if (!detailView) return {};
  if (/basis/i.test(detailView)) {
    return { ...commodity_visibility_fields, implied_basis: false }
  }
  if (/hta/i.test(detailView)) {
    return { ...commodity_visibility_fields, implied_futures: false }
  }
  if (/hta|dpq-futures/i.test(detailView)) {
    return { ...commodity_visibility_fields, implied_futures: false }
  }

  return { ...commodity_visibility_fields };
}
